export type OrdinalResolutionAction = 'fetch_answer' | 'ensure_thread'

type IdentifiableAnswer = {
    id?: string | number | null
}

type ShareAttributedAnswer = {
    user?: { name?: string | null } | null
    user_name?: string | null
}

export async function fetchAndCacheOrdinalAnswer<T extends IdentifiableAnswer>(
    answerId: string,
    fetchAnswer: (id: string) => Promise<T | null | undefined>,
    cacheAnswer: (answer: T) => unknown,
): Promise<T | null> {
    const answer = await fetchAnswer(answerId)
    if (!answer?.id) return null

    cacheAnswer(answer)
    return answer
}

// Wrapper answers remain system-owned structurally, but ordinal share copy is
// credited to the current artifact holder shown on the stream tile.
export function withOrdinalShareOwner<T extends ShareAttributedAnswer>(
    answer: T,
    ownerName?: string | null,
): T {
    const name = ownerName?.trim()
    if (!name) return answer

    return {
        ...answer,
        user_name: name,
        user: answer.user ? { ...answer.user, name } : answer.user,
    } as T
}

type OrdinalResolutionOptions = {
    knownAnswerId?: string | null
    knownChildQuestId?: string | null
    requireThread?: boolean
}

export function getOrdinalResolutionAction({
    knownAnswerId,
    requireThread = false,
}: OrdinalResolutionOptions): OrdinalResolutionAction {
    // Reply always resolves through ensure_thread so a native ordinal cannot
    // reuse the conversation where it was minted.
    if (requireThread) return 'ensure_thread'
    if (knownAnswerId) return 'fetch_answer'
    return 'ensure_thread'
}
